/**
 * Input Adapter for shadcn
 * 
 * This component adapts the shadcn input to fit our design system.
 */
import * as React from "react"
import { InputBaseProps } from "@repo/design-system/types"
import { Input as ShadcnInput } from "../input"
import { cn } from "@/lib/utils"

// Define properties specific to our premium input implementation
export interface InputProps extends InputBaseProps {
  // Additional app-specific props can be added here
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  helperText?: string;
  state?: 'error' | 'success' | 'warning';
  inputSize?: 'default' | 'sm' | 'lg' | 'md' | 'xl';
  radius?: 'none' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | 'full';
}

/**
 * Input adapter for shadcn
 * 
 * This component adapts the shadcn input to fit our design system.
 * 
 * @example
 * ```tsx
 * <Input 
 *   variant="default"
 *   size="md"
 *   placeholder="Enter text"
 *   leftIcon={<SearchIcon />}
 * />
 * ```
 */
const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({
    className,
    variant = "default",
    inputSize = "default",
    type = "text",
    state,
    label,
    helperText,
    error,
    disabled,
    value,
    defaultValue,
    onValueChange,
    onChange,
    required,
    leftIcon,
    rightIcon,
    id,
    radius,
    ...props
  }, ref) => {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange?.(e);
      onValueChange?.(e.target.value);
    };

    // Get border radius class
    const radiusClasses = {
      none: "premium-radius-none",
      sm: "premium-radius-sm",
      md: "premium-radius-md",
      lg: "premium-radius-lg",
      xl: "premium-radius-xl",
      "2xl": "premium-radius-2xl",
      "3xl": "premium-radius-3xl",
      full: "premium-radius-full"
    };
    
    const radiusClass = radius 
      ? radiusClasses[radius] 
      : "premium-radius-input"; // Component-specific default

    return (
      <div className="relative w-full">
        {label && (
          <label htmlFor={id} className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}
        <div className="relative">
          {leftIcon && (
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              {leftIcon}
            </div>
          )}
          <ShadcnInput
            ref={ref}
            type={type}
            className={cn(
              leftIcon && "pl-10",
              rightIcon && "pr-10",
              className,
              radiusClass
            )}
            variant={variant}
            inputSize={inputSize}
            disabled={disabled}
            value={value}
            defaultValue={defaultValue}
            onChange={handleChange}
            required={required}
            id={id}
            {...props}
          />
          {rightIcon && (
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
              {rightIcon}
            </div>
          )}
        </div>
        {(helperText || error) && (
          <p className={cn(
            "mt-1 text-sm",
            error ? "text-red-500" : "text-gray-500 dark:text-gray-400"
          )}>
            {error || helperText}
          </p>
        )}
      </div>
    )
  }
)
Input.displayName = "Input"

export { Input }
